import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { X, MapPin, ChevronDown, RotateCcw } from 'lucide-react';
import { CommanServices } from '@/services/commanServices';

const CascadingLocationSelector = ({ 
  value = null, 
  onChange = () => {}, 
  className = "",
  disabled = false 
}) => {
  const [rootLocations, setRootLocations] = useState([]);
  const [selectedRoot, setSelectedRoot] = useState(null);
  const [cascadeLevels, setCascadeLevels] = useState([]);
  const [selectedPath, setSelectedPath] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchCache, setSearchCache] = useState(new Map());

  // Initialize from props
  useEffect(() => {
    if (value && value.root_id && rootLocations.length > 0) {
      // Reconstruct the cascade from the stored value
      reconstructFromValue(value);
    } else if (!value) {
      // Clear the selector
      clearSelection();
    }
  }, [value, rootLocations]);

  // Fetch root locations on mount
  useEffect(() => {
    fetchRootLocations();
  }, []);

  const reconstructFromValue = async (storedValue) => {
    try {
      const rootLocation = rootLocations.find(root => root.id === storedValue.root_id);
      if (!rootLocation) return;

      setSelectedRoot(rootLocation);
      
      if (storedValue.path && storedValue.path.length > 0) {
        // Reconstruct the cascade path
        let currentLocationId = storedValue.root_id;
        const reconstructedLevels = [];
        const reconstructedPath = [];

        for (let i = 0; i < storedValue.path.length; i++) {
          const pathItem = storedValue.path[i];
          
          // Fetch children of current location
          const childrenData = await fetchLocationChildren(currentLocationId);
          if (childrenData && childrenData.children_by_type) {
            // Find the type group that contains our target
            for (const [typeName, typeGroup] of Object.entries(childrenData.children_by_type)) {
              const targetChild = typeGroup.children.find(child => child.id === pathItem.id);
              if (targetChild) {
                const level = {
                  type: typeName,
                  children: typeGroup.children,
                  selectedValue: targetChild,
                  selectedScope: pathItem.scope || 'EXACT'
                };
                reconstructedLevels.push(level);
                reconstructedPath.push(pathItem);
                currentLocationId = pathItem.id;
                break;
              }
            }
          }
        }

        setCascadeLevels(reconstructedLevels);
        setSelectedPath(reconstructedPath);
      } else if (rootLocation.has_children) {
        // Just selected root, show first level
        const childrenData = await fetchLocationChildren(rootLocation.id);
        if (childrenData && childrenData.children_by_type) {
          const levels = Object.values(childrenData.children_by_type).map(typeGroup => ({
            type: typeGroup.type,
            children: typeGroup.children,
            selectedValue: null,
            selectedScope: 'ALL'
          }));
          setCascadeLevels(levels);
        }
      }
    } catch (err) {
      console.error('Error reconstructing cascade:', err);
    }
  };

  const fetchRootLocations = async () => {
    try {
      setLoading(true);
      const response = await CommanServices.getlocationsroots();
      if (!response.status){
        alert('Failed to fetch root locations')
      }else{
        const roots = await response.data;
        setRootLocations(roots);
      }
    } catch (err) {
      setError('Failed to load root locations');
      console.error('Error fetching root locations:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationChildren = async (locationId) => {
    // Check cache first
    if (searchCache.has(locationId)) {
      return searchCache.get(locationId);
    }

    try {
      const response = await CommanServices.getlocationschildren(locationId);
      if (!response.status){
        alert('Failed to fetch root locations')
      }else{
        const data = await response.data;
        // Cache the result
        setSearchCache(prev => new Map(prev.set(locationId, data)));
        return data;
      }
      
    } catch (err) {
      console.error('Error fetching location children:', err);
      return null;
    }
  };

  const handleRootSelection = async (rootLocation) => {
    if (!rootLocation) {
      // Clear selection
      setSelectedRoot(null);
      setCascadeLevels([]);
      setSelectedPath([]);
      onChange(null);
      return;
    }

    setSelectedRoot(rootLocation);
    setSelectedPath([]);
    
    // Fetch children to build first cascade level
    if (rootLocation.has_children) {
      const childrenData = await fetchLocationChildren(rootLocation.id);
      if (childrenData && childrenData.children_by_type) {
        const levels = Object.values(childrenData.children_by_type).map(typeGroup => ({
          type: typeGroup.type,
          children: typeGroup.children,
          selectedValue: null,
          selectedScope: 'ALL' // Default to "Select all"
        }));
        setCascadeLevels(levels);
      } else {
        setCascadeLevels([]);
      }
    } else {
      setCascadeLevels([]);
    }

    // Update the onChange with root selection  
    const selection = buildSelectionObject([], rootLocation.has_children ? [{ selectedScope: 'ALL', type: 'All Locations' }] : []);
    onChange(selection);
  };

  const handleLevelSelection = async (levelIndex, selectedOption) => {
    const newPath = [...selectedPath];
    const newLevels = [...cascadeLevels];

    if (selectedOption === 'ALL') {
      // Select all at this level
      newLevels[levelIndex].selectedValue = null;
      newLevels[levelIndex].selectedScope = 'ALL';
      
      // Clear all levels below this one
      newPath.splice(levelIndex);
      newLevels.splice(levelIndex + 1);
      
    } else {
      // Select specific item
      const selectedItem = newLevels[levelIndex].children.find(child => child.id === selectedOption);
      if (!selectedItem) return;

      newLevels[levelIndex].selectedValue = selectedItem;
      newLevels[levelIndex].selectedScope = 'EXACT';
      
      // Update path
      newPath[levelIndex] = {
        id: selectedItem.id,
        type: selectedItem.type,
        value: selectedItem.value,
        label: selectedItem.label,
        scope: 'EXACT'
      };
      
      // Clear levels below this one
      newPath.splice(levelIndex + 1);
      newLevels.splice(levelIndex + 1);

      // Fetch children for next level if they exist
      if (selectedItem.has_children) {
        const childrenData = await fetchLocationChildren(selectedItem.id);
        if (childrenData && childrenData.children_by_type) {
          const nextLevels = Object.values(childrenData.children_by_type).map(typeGroup => ({
            type: typeGroup.type,
            children: typeGroup.children,
            selectedValue: null,
            selectedScope: 'ALL'
          }));
          newLevels.push(...nextLevels);
        }
      }
    }

    setSelectedPath(newPath);
    setCascadeLevels(newLevels);

    // Build the final selection object using user's preferred format
    const selection = buildSelectionObject(newPath, newLevels);
    onChange(selection);
  };

  const buildSelectionObject = (path, levels) => {
    if (!selectedRoot) return null;

    // Find the lowest level with a specific (non-wildcard) selection
    let lowestSpecificLevel = -1;
    let leafSelection = null;
    
    for (let i = levels.length - 1; i >= 0; i--) {
      if (levels[i].selectedScope === 'EXACT' && levels[i].selectedValue) {
        lowestSpecificLevel = i;
        leafSelection = {
          id: levels[i].selectedValue.id,
          scope: 'EXACT'
        };
        break;
      }
    }

    // Build wildcards array for levels that have "Select all"
    const wildcards = [];
    for (let i = 0; i < levels.length; i++) {
      if (levels[i].selectedScope === 'ALL') {
        wildcards.push({
          level: levels[i].type,
          mode: 'ALL'
        });
      }
    }

    // Determine match mode - simpler alternative as mentioned in spec
    let matchMode = 'INCLUDE_CHILDREN'; // Default to include children
    if (lowestSpecificLevel >= 0) {
      // We have a specific selection, check if there are children levels below it
      const hasWildcardsBelow = levels.slice(lowestSpecificLevel + 1).some(level => level.selectedScope === 'ALL');
      matchMode = hasWildcardsBelow ? 'INCLUDE_CHILDREN' : 'EXACT';
    }

    const selection = {
      location: {
        root_id: selectedRoot.id,
        path: path.map(item => ({
          id: item.id,
          scope: item.scope || 'EXACT'
        })),
        leaf: leafSelection,
        wildcards: wildcards
      },
      // Simpler alternative format
      node_id: leafSelection?.id || selectedRoot.id,
      match_mode: matchMode,
      
      // Display metadata
      display_label: getDisplayLabel(),
      root_label: selectedRoot.label
    };

    return selection;
  };

  const getDisplayLabel = () => {
    if (!selectedRoot) return "";
    
    // Generate full hierarchical path like auto_label format
    const pathSegments = [];
    
    // Add root location
    pathSegments.push(selectedRoot.label);
    
    // Add each level in the selected path
    selectedPath.forEach(pathItem => {
      pathSegments.push(pathItem.label);
    });
    
    // Return complete hierarchical path with " – " separator (same as Locations & Zones auto_label)
    return pathSegments.join(' – ');
  };

  const getDisplayValue = () => {
    if (!selectedRoot) return "Select location...";
    
    // Use the complete hierarchical path for display
    const fullPath = getDisplayLabel();
    
    // Determine scope tag
    let scopeTag = "Include children";
    const lastSpecific = selectedPath[selectedPath.length - 1];
    if (lastSpecific) {
      scopeTag = lastSpecific.scope === 'EXACT' ? "Exact" : "Include children";
    }
    
    // Check if there are any wildcards at the current level
    const hasWildcards = cascadeLevels.some(level => level.selectedScope === 'ALL');
    if (hasWildcards && !lastSpecific) {
      scopeTag = "Include children"; 
    }
    
    return (
      <div className="flex items-center justify-between w-full">
        <span className="truncate" title={fullPath}>{fullPath}</span>
        <Badge variant="secondary" className="ml-2 shrink-0 text-xs">
          {scopeTag}
        </Badge>
      </div>
    );
  };

  const clearSelection = () => {
    setSelectedRoot(null);
    setCascadeLevels([]);
    setSelectedPath([]);
    onChange(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4 text-sm text-gray-500">
        Loading locations...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-4 text-sm text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Root Location Selection */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium mb-2">
          <MapPin className="w-4 h-4" />
          Location
        </label>
        
        <div className="flex gap-2">
          <Select 
            value={selectedRoot?.id || "__WILDCARD__"} 
            onValueChange={(value) => {
              if (!value || value === "__WILDCARD__") {
                handleRootSelection(null);
              } else {
                const rootLocation = rootLocations.find(root => root.id === value);
                handleRootSelection(rootLocation);
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select root location...">
                {selectedRoot ? getDisplayValue() : "Select root location..."}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__WILDCARD__">
                <span className="text-gray-500 italic">All locations (wildcard)</span>
              </SelectItem>
              {rootLocations.map((root) => (
                <SelectItem key={root.id} value={root.id}>
                  <div className="flex items-center justify-between w-full">
                    <span>{root.label}</span>
                    {root.has_children && (
                      <ChevronDown className="w-3 h-3 ml-2 text-gray-400" />
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {selectedRoot && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearSelection}
              disabled={disabled}
              className="shrink-0"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Cascading Level Selectors */}
      {cascadeLevels.map((level, levelIndex) => (
        <div key={`${level.type}-${levelIndex}`}>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium">
              {level.type}
            </label>
            {level.selectedScope === 'EXACT' && level.selectedValue && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleLevelSelection(levelIndex, 'ALL')}
                disabled={disabled}
                className="text-xs h-6 px-2"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Reset to All
              </Button>
            )}
          </div>
          
          <Select
            value={level.selectedValue?.id || "ALL"}
            onValueChange={(value) => handleLevelSelection(levelIndex, value)}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue>
                {level.selectedScope === 'ALL' ? (
                  <span className="text-blue-600 font-medium">✨ Select all</span>
                ) : (
                  level.selectedValue?.label || "Select..."
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">
                <span className="text-blue-600 font-medium">✨ Select all</span>
              </SelectItem>
              {level.children.map((child) => (
                <SelectItem key={child.id} value={child.id}>
                  <div className="flex items-center justify-between w-full">
                    <span className="truncate">{child.label || `${child.type}: ${child.value}`}</span>
                    {child.has_children && (
                      <ChevronDown className="w-3 h-3 ml-2 text-gray-400 shrink-0" />
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {level.children.length === 0 && (
            <div className="text-sm text-gray-500 mt-1 italic">
              No sub-locations
            </div>
          )}
        </div>
      ))}

      {/* Debug Info (development only) */}
      {process.env.NODE_ENV === 'development' && selectedRoot && (
        <details className="text-xs text-gray-500">
          <summary>Debug Info</summary>
          <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">
            {JSON.stringify({ selectedRoot, selectedPath, cascadeLevels }, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};

export default CascadingLocationSelector;